import React, { useMemo, useState, useEffect, useRef } from "react";
import { MessageCircle, ArrowRight, Brain, Sparkles, Loader2, Send, History, Trash2 } from "lucide-react";
import {
  capacityRetention,
  degradationVelocity,
  chargingQualityScore,
  thermalStressIndex,
  batteryRiskScore,
  futureHealthForecast,
} from "../../lib/batteryAnalytics";
import { VEHICLE_USAGE_HINTS } from "./views/_shared";
import { coachInsights } from "../../lib/insightSummaries";
import { warrantyFor } from "../../lib/brand";
import { COLOR } from "./pillars/_cards";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// localStorage keyed per VIN so switching vehicles never leaks Q&A into
// a different pack. Cap at MAX_HISTORY entries to keep the storage
// footprint bounded.
const COACH_HISTORY_KEY = "eviq.coach_history_v1";
const MAX_HISTORY = 20;

function readCoachHistory(vin) {
  if (!vin) return [];
  try {
    const raw = window.localStorage.getItem(COACH_HISTORY_KEY);
    if (!raw) return [];
    const bag = JSON.parse(raw);
    const arr = bag?.[vin];
    return Array.isArray(arr) ? arr : [];
  } catch (_e) {
    return [];
  }
}

function writeCoachHistory(vin, list) {
  if (!vin) return;
  try {
    const raw = window.localStorage.getItem(COACH_HISTORY_KEY);
    const bag = raw ? JSON.parse(raw) : {};
    bag[vin] = list.slice(0, MAX_HISTORY);
    window.localStorage.setItem(COACH_HISTORY_KEY, JSON.stringify(bag));
  } catch (_e) {
    /* storage full — silent */
  }
}

/**
 * Build a PassportSnapshot payload from the merged vehicle context —
 * matches the pydantic PassportSnapshot on the backend so /battery/coach
 * (and /battery/certify + /battery/passport) accept it as-is.
 */
function buildSnapshot(vehicle, climate) {
  const risk = batteryRiskScore(vehicle, climate);
  const forecast = futureHealthForecast(vehicle);
  return {
    vin: vehicle.vin || "UNKNOWN",
    year: vehicle.year || new Date().getFullYear() - 3,
    model: vehicle.model || "Model 3",
    trim: vehicle.trim || null,
    soh_pct: vehicle.sohCurrent ?? 92,
    delta_30d_pct: vehicle.delta30d ?? -0.2,
    epa_when_new_mi: vehicle.epaRange || 300,
    pack_kwh: vehicle.packKwh || 75,
    warranty_years: warrantyFor(vehicle).years,
    warranty_miles: warrantyFor(vehicle).miles,
    warranty_threshold_pct: warrantyFor(vehicle).threshold,
    risk_score: risk.value,
    risk_band: risk.band,
    forecast_3yr_soh: forecast.y3,
    forecast_8yr_soh: forecast.y8,
  };
}

/**
 * BatteryCoachCard — plain-English Q&A about the user's *actual* pack.
 *
 * Four preset questions surface as chips. Selecting one reveals an
 * explanation generated from the vehicle's live analytics (via
 * batteryAnalytics.js). Each answer references specific numbers from
 * the user's Tesla telemetry — no generic "batteries degrade over
 * time" filler.
 *
 * Deterministic-first, LLM-later: once we have historical data pulled
 * into the backend, we'll swap the answer body for a Claude call
 * grounded in the same numbers. The chip contract stays identical so
 * the UI doesn't need to change.
 */
export const BatteryCoachCard = ({ vehicle, climate, stateCode }) => {
  const isFord = vehicle?.brand === "ford";
  const fastChargeVerb = isFord ? "DC fast-charge" : "Supercharge";
  const hints = VEHICLE_USAGE_HINTS[vehicle.id] || VEHICLE_USAGE_HINTS["model-3-lr"];
  const [selected, setSelected] = useState(null);
  const [llmAnswer, setLlmAnswer] = useState(null);      // when live LLM reply lands
  const [llmLoading, setLlmLoading] = useState(false);
  const [llmSource, setLlmSource] = useState(null);

  // Free-form question state.
  const [freeQuestion, setFreeQuestion] = useState("");
  const [freeAsked, setFreeAsked] = useState("");        // the question the user actually submitted
  const [freeError, setFreeError] = useState("");

  // Per-VIN Q&A history — persists across reloads/tab switches.
  const vin = vehicle?.vin || null;
  const [history, setHistory] = useState(() => readCoachHistory(vin));
  const [showHistory, setShowHistory] = useState(false);
  // If the VIN changes (switched vehicle), reload history for the new pack.
  const lastVinRef = useRef(vin);
  useEffect(() => {
    if (lastVinRef.current !== vin) {
      lastVinRef.current = vin;
      setHistory(readCoachHistory(vin));
      setSelected(null);
      setLlmAnswer(null);
      setLlmSource(null);
      setFreeAsked("");
    }
  }, [vin]);

  const pushHistory = (q, a, source) => {
    if (!vin || !q || !a) return;
    setHistory((prev) => {
      // Dedupe consecutive identical questions.
      const filtered = prev.filter((h) => h.q !== q);
      const next = [{ q, a, source: source || null, ts: Date.now() }, ...filtered].slice(0, MAX_HISTORY);
      writeCoachHistory(vin, next);
      return next;
    });
  };

  const clearHistory = () => {
    setHistory([]);
    writeCoachHistory(vin, []);
    setShowHistory(false);
  };

  // Precompute all the numbers the answers reference so we render fast.
  const stats = useMemo(() => {
    const soh = capacityRetention(vehicle);
    const vel = degradationVelocity(vehicle);
    const charging = chargingQualityScore(vehicle, hints);
    const thermal = thermalStressIndex(vehicle, climate);
    const risk = batteryRiskScore(vehicle, climate);
    const forecast = futureHealthForecast(vehicle);
    return { soh, vel, charging, thermal, risk, forecast };
  }, [vehicle, climate, hints]);

  const QUESTIONS = [
    {
      id: "why-drop",
      q: "Why did my battery health drop?",
      answer: () => {
        const monthlyPct = (stats.vel).toFixed(2);
        const drivers = [];
        if (stats.thermal > 55) drivers.push("thermal cycling from your climate");
        if (stats.charging < 75) drivers.push("your DC-fast-charge share");
        if (!drivers.length) drivers.push("normal calendar aging");
        return `You're at ${stats.soh}% SoH and losing about ${monthlyPct}% per month — that's ${stats.vel < 0.25 ? "well inside" : "slightly above"} the industry median of 0.20 %/mo. The main drivers right now are ${drivers.join(" and ")}. Nothing dramatic — the trend is smooth, not step-shaped, which rules out a single bad session.`;
      },
    },
    {
      id: "charge-slower",
      q: "Why is my charging slower today?",
      answer: () => {
        const tempTag =
          typeof vehicle.outsideTempC === "number"
            ? Math.round((vehicle.outsideTempC * 9) / 5 + 32)
            : null;
        const tempPart = tempTag == null
          ? "we don't have a live outside-temp reading yet"
          : tempTag <= 40
          ? `it's ${tempTag}°F outside — cold cells refuse full DC current until they warm up`
          : tempTag >= 95
          ? `it's ${tempTag}°F outside — Tesla intentionally throttles current to protect the pack`
          : `outside temp (${tempTag}°F) is in the sweet spot, so weather isn't the culprit`;
        const socPart = typeof vehicle.batteryPct === "number" && vehicle.batteryPct > 55
          ? " Above ~55% SoC Tesla naturally tapers current to preserve cell chemistry."
          : "";
        return `Two things dominate charging speed: cell temperature and current SoC. ${tempPart}. Preconditioning for 20-25 minutes before a Supercharger stop typically restores 90%+ of peak charging speed.${socPart}`;
      },
    },
    {
      id: "road-trip",
      q: "Will a road trip hurt my battery?",
      answer: () => {
        const perLegCost = stats.charging < 75 ? "0.02–0.04%" : "0.01–0.02%";
        return `A single road trip contributes about ${perLegCost} of pack degradation — measurable but tiny in the grand scheme (your pack loses ${(stats.vel).toFixed(2)}% per month regardless). What matters more is what you do at the destination: avoid leaving the car parked at 100% for days, precondition before every ${isFord ? "fast-charge" : "Supercharger"} stop, and skip 100% charges unless you truly need the range. Do those three and your projected 3-year SoH stays at ~${stats.forecast.y3}%.`;
      },
    },
    {
      id: "supercharge-daily",
      q: `What if I ${fastChargeVerb} every day?`,
      answer: () => {
        const three = (stats.forecast.y3 - 2.4).toFixed(1);
        return `Daily DC-fast-charging shifts your degradation velocity from ${stats.vel.toFixed(2)}% to roughly ${(stats.vel * 1.4).toFixed(2)}% per month — a 40% faster pace. Over 3 years that's the difference between ~${stats.forecast.y3}% SoH (your current trajectory) and ~${three}%. Not catastrophic, but you'd cross the warranty floor 12-18 months earlier than the current path. Use L2 at home whenever you're not on a road trip.`;
      },
    },
    {
      id: "leave-100",
      q: "What if I leave it at 100%?",
      answer: () => {
        return `Sitting at 100% SoC for extended periods is one of the biggest levers on calendar aging — the high state-of-charge holds the electrolyte at elevated stress. Tesla explicitly recommends charging to 80-90% for daily use and hitting 100% only right before a long drive. If you did daily 100% charges for a year, expect ~1.5-2× the current ${stats.vel.toFixed(2)}%/mo velocity, so year-3 SoH lands closer to ${(stats.forecast.y3 - 3.5).toFixed(1)}% instead of ${stats.forecast.y3}%.`;
      },
    },
    {
      id: "sell-now",
      q: "Should I sell now or hold?",
      answer: () => {
        return `Your pack is retaining ${stats.soh}% of capacity — the resale market currently pays roughly $210 per kWh of retained capacity. Selling in the next 6 months means you're transacting close to today's number. Holding another 2 years means an extra ${(stats.vel * 24).toFixed(1)}% of degradation, which typically translates to $1,200-$2,000 less resale. That's a personal decision — but the pack itself is still comfortably above every historical resale threshold.`;
      },
    },
  ];

  return (
    <div
      className="rounded-xl border p-4"
      style={{
        borderColor: "rgba(0,229,255,0.28)",
        background: "linear-gradient(135deg, rgba(0,229,255,0.06), rgba(57,255,20,0.03))",
      }}
      data-testid="battery-coach-card"
    >
      <div className="flex items-center gap-3 mb-3">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center border"
          style={{ background: "rgba(0,229,255,0.14)", borderColor: "rgba(0,229,255,0.45)" }}
        >
          <MessageCircle className="w-4 h-4" style={{ color: COLOR.cyan }} strokeWidth={2.4} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">
            AI Battery Coach
          </div>
          <div className="text-sm text-white font-semibold leading-snug">
            Ask about your pack — in plain English.
          </div>
        </div>
        {history.length > 0 && (
          <button
            type="button"
            onClick={() => setShowHistory((s) => !s)}
            data-testid="coach-history-toggle"
            aria-label={showHistory ? "Hide history" : "Show history"}
            title={`${history.length} saved · click to ${showHistory ? "hide" : "show"}`}
            className="shrink-0 inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[10px] font-mono uppercase tracking-wider text-zinc-300 hover:text-white hover:bg-white/[0.05] transition-colors"
            style={{ borderColor: "rgba(255,255,255,0.12)" }}
          >
            <History className="w-3 h-3" strokeWidth={2.4} />
            {history.length}
          </button>
        )}
      </div>

      {/* Question chips */}
      <div className="flex flex-wrap gap-1.5" data-testid="battery-coach-chips">
        {QUESTIONS.map((q) => (
          <button
            key={q.id}
            type="button"
            onClick={async () => {
              // Toggle same chip = clear.
              if (selected === q.id) { setSelected(null); setLlmAnswer(null); return; }
              setSelected(q.id);
              setLlmAnswer(null);
              setLlmSource(null);
              setFreeAsked("");
              // Only call the LLM when we have a real Tesla connection.
              if (!vehicle?.live || !vehicle?.vin) return;
              setLlmLoading(true);
              try {
                const res = await fetch(`${API_BASE}/api/battery/coach`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    question: q.q,
                    vehicle_context: buildSnapshot(vehicle, climate),
                    insights: coachInsights(vehicle, climate, Math.abs(vehicle.delta30d || 0.2)),
                  }),
                });
                if (res.ok) {
                  const data = await res.json();
                  setLlmAnswer(data?.answer || null);
                  setLlmSource(data?.source || null);
                  if (data?.answer) pushHistory(q.q, data.answer, data?.source);
                }
              } catch (_e) {
                // Silent — deterministic fallback shows automatically.
              } finally {
                setLlmLoading(false);
              }
            }}
            data-testid={`coach-chip-${q.id}`}
            className="text-[11px] rounded-full border px-2.5 py-1 transition-colors"
            style={{
              borderColor: selected === q.id ? "rgba(0,229,255,0.55)" : "rgba(255,255,255,0.10)",
              background: selected === q.id ? "rgba(0,229,255,0.12)" : "rgba(255,255,255,0.03)",
              color: selected === q.id ? "#00E5FF" : "rgba(255,255,255,0.85)",
            }}
          >
            {q.q}
          </button>
        ))}
      </div>

      {/* Free-form ask box — plain-English question about the user's pack.
          Hits the same /api/battery/coach endpoint as the chips (which is
          LLM-backed on Claude Sonnet 4.5 when live, deterministic fallback
          otherwise). We ONLY submit when the Tesla session is live so the
          coach has real vehicle context to reason about. */}
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setFreeError("");
          const trimmed = freeQuestion.trim();
          if (!trimmed) return;
          if (trimmed.length > 300) {
            setFreeError("Please keep it under 300 characters.");
            return;
          }
          if (!vehicle?.live || !vehicle?.vin) {
            setFreeError("Sign in with Tesla so the coach can reason about your pack.");
            return;
          }
          setSelected("__free__");
          setFreeAsked(trimmed);
          setLlmAnswer(null);
          setLlmSource(null);
          setLlmLoading(true);
          try {
            const res = await fetch(`${API_BASE}/api/battery/coach`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                question: trimmed,
                vehicle_context: buildSnapshot(vehicle, climate),
                insights: coachInsights(vehicle, climate, Math.abs(vehicle.delta30d || 0.2)),
              }),
            });
            if (!res.ok) {
              throw new Error("Coach request failed");
            }
            const data = await res.json();
            setLlmAnswer(data?.answer || null);
            setLlmSource(data?.source || null);
            if (data?.answer) pushHistory(trimmed, data.answer, data?.source);
          } catch (_err) {
            setFreeError("Couldn't reach the coach — try again in a moment.");
          } finally {
            setLlmLoading(false);
          }
        }}
        className="mt-3 rounded-lg border p-2.5"
        style={{
          borderColor: "rgba(0,229,255,0.22)",
          background: "rgba(0,229,255,0.04)",
        }}
        data-testid="battery-coach-ask-form"
      >
        <label className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500 block mb-1.5">
          Or ask anything about your pack
        </label>
        <div className="flex items-end gap-2">
          <textarea
            value={freeQuestion}
            onChange={(e) => setFreeQuestion(e.target.value)}
            placeholder={
              vehicle?.live
                ? "e.g. Is my pack degrading faster than the fleet average?"
                : "Sign in with Tesla to ask the coach…"
            }
            disabled={!vehicle?.live || llmLoading}
            rows={2}
            maxLength={300}
            data-testid="battery-coach-input"
            className="flex-1 rounded-md border border-white/10 bg-black/40 text-[12px] text-white placeholder-zinc-500 px-2 py-1.5 leading-relaxed resize-none focus:outline-none focus:border-[#00E5FF]/50 focus:ring-1 focus:ring-[#00E5FF]/30 disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={!vehicle?.live || !freeQuestion.trim() || llmLoading}
            data-testid="battery-coach-submit"
            aria-label="Ask the coach"
            className="shrink-0 rounded-md brand-gradient text-black px-3 py-1.5 text-[12px] font-semibold inline-flex items-center gap-1.5 hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {llmLoading && selected === "__free__" ? (
              <Loader2 className="w-3 h-3 animate-spin" strokeWidth={2.5} />
            ) : (
              <Send className="w-3 h-3" strokeWidth={2.5} />
            )}
            Ask
          </button>
        </div>
        <div className="flex items-center justify-between mt-1.5">
          <div className="text-[10px] text-zinc-500">
            {freeQuestion.length > 0 && `${freeQuestion.length}/300`}
          </div>
          {freeError && (
            <div
              className="text-[10px] text-[#FF6B57]"
              data-testid="battery-coach-error"
            >
              {freeError}
            </div>
          )}
        </div>
      </form>

      {/* Selected answer — renders for both preset chips and free-form questions */}
      {selected && (
        <div
          className="mt-3 rounded-lg border p-3"
          style={{ borderColor: "rgba(0,229,255,0.20)", background: "rgba(0,229,255,0.04)" }}
          data-testid="battery-coach-answer"
        >
          {selected === "__free__" && freeAsked && (
            <div
              className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1.5 italic"
              data-testid="battery-coach-echo-question"
            >
              You asked: "{freeAsked}"
            </div>
          )}
          <div className="flex items-start gap-2">
            {llmLoading ? (
              <Loader2 className="w-3 h-3 mt-1 shrink-0 animate-spin" style={{ color: COLOR.cyan }} strokeWidth={2.4} />
            ) : (
              <Sparkles className="w-3 h-3 mt-1 shrink-0" style={{ color: COLOR.cyan }} strokeWidth={2.4} />
            )}
            <div className="min-w-0 flex-1">
              <div className="text-[12px] text-zinc-100 leading-relaxed whitespace-pre-wrap">
                {llmLoading
                  ? "Thinking about your pack…"
                  : selected === "__free__"
                  ? (llmAnswer || "Couldn't generate an answer — please try again.")
                  : (llmAnswer || QUESTIONS.find((q) => q.id === selected)?.answer())}
              </div>
              {llmSource && (
                <div className="font-mono text-[9px] text-zinc-500 uppercase tracking-wider mt-2">
                  {llmSource === "claude" ? "Claude Sonnet 4.5 · personalized" : llmSource === "deterministic" ? "Personalized answer" : "Deterministic fallback"}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Q&A history — persisted per VIN via localStorage. Survives
          reloads, tab switches, and PWA relaunches. */}
      {showHistory && history.length > 0 && (
        <div
          className="mt-3 rounded-lg border p-3 space-y-2"
          style={{ borderColor: "rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.02)" }}
          data-testid="battery-coach-history"
        >
          <div className="flex items-center justify-between">
            <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500 inline-flex items-center gap-1.5">
              <History className="w-3 h-3" strokeWidth={2.4} />
              Saved on this pack
            </div>
            <button
              type="button"
              onClick={clearHistory}
              data-testid="coach-history-clear"
              className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-zinc-500 hover:text-[#FF6B57] transition-colors"
            >
              <Trash2 className="w-3 h-3" strokeWidth={2.4} />
              Clear
            </button>
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
            {history.map((h, idx) => (
              <div
                key={`${h.ts}-${idx}`}
                className="rounded-md border p-2"
                style={{ borderColor: "rgba(255,255,255,0.06)", background: "rgba(255,255,255,0.02)" }}
                data-testid={`coach-history-item-${idx}`}
              >
                <div className="text-[11px] text-zinc-200 font-medium leading-snug">
                  {h.q}
                </div>
                <div className="text-[11px] text-zinc-400 leading-relaxed mt-1 whitespace-pre-wrap">
                  {h.a}
                </div>
                <div className="font-mono text-[9px] text-zinc-600 uppercase tracking-wider mt-1.5">
                  {new Date(h.ts).toLocaleString()}
                  {h.source ? ` · ${h.source === "claude" ? "Claude" : "fallback"}` : ""}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
